var __rest = (this && this.__rest) || function (s, e) {
    var t = {};
    for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p) && e.indexOf(p) < 0)
        t[p] = s[p];
    if (s != null && typeof Object.getOwnPropertySymbols === "function")
        for (var i = 0, p = Object.getOwnPropertySymbols(s); i < p.length; i++) {
            if (e.indexOf(p[i]) < 0 && Object.prototype.propertyIsEnumerable.call(s, p[i]))
                t[p[i]] = s[p[i]];
        }
    return t;
};
import { CARD_VP_DISPLAY_FIELDS } from '../apiTypes.js';
import { isApiCardLandingPageLink, isApiSlimLandingPageLink, } from './landingPageLinkEnrichment.js';
function isNonEmptySharedProductRecord(shared) {
    return shared != null && Object.keys(shared).length > 0;
}
/**
 * Combines shared display rows with the link's own map. Shared rows win; link-only
 * product ids are kept.
 */
function mergeVirtualProductDisplayDataMaps(linkDisplayByProductId, bucketDisplayByProductId) {
    const productIds = unionProductIds(linkDisplayByProductId, bucketDisplayByProductId);
    const merged = {};
    for (const productId of productIds) {
        merged[productId] = mergeOneProductDisplayData(productId, linkDisplayByProductId, bucketDisplayByProductId);
    }
    return merged;
}
function unionProductIds(linkMap, bucketMap) {
    const keys = Object.keys(bucketMap).concat(linkMap ? Object.keys(linkMap) : []);
    const seen = {};
    const out = [];
    for (let i = 0; i < keys.length; i++) {
        const k = keys[i];
        if (!seen[k]) {
            seen[k] = true;
            out.push(k);
        }
    }
    return out;
}
function mergeOneProductDisplayData(productId, linkMap, bucketMap) {
    const bucketRow = bucketMap[productId];
    if (bucketRow != null) {
        return bucketRow;
    }
    return linkMap === null || linkMap === void 0 ? void 0 : linkMap[productId];
}
function slimVirtualProductDisplayDataForCard(data) {
    const slimmed = {};
    for (const key of CARD_VP_DISPLAY_FIELDS) {
        if (data[key] !== undefined) {
            Object.assign(slimmed, { [key]: data[key] });
        }
    }
    return slimmed;
}
function slimVirtualProductDisplayDataMapForCard(map) {
    if (map == null)
        return undefined;
    return Object.fromEntries(Object.entries(map).map(([id, data]) => [
        id,
        slimVirtualProductDisplayDataForCard(data),
    ]));
}
function overlayCommerceSharedFields(shared) {
    return Object.assign(Object.assign(Object.assign(Object.assign(Object.assign(Object.assign(Object.assign(Object.assign(Object.assign({}, (shared.variantInfo !== undefined
        ? { variantInfo: shared.variantInfo }
        : {})), (shared.optionInfo !== undefined
        ? { optionInfo: shared.optionInfo }
        : {})), (shared.sellingPlanGroups !== undefined
        ? { sellingPlanGroups: shared.sellingPlanGroups }
        : {})), (shared.sellingPlanGroupsByProductId !== undefined
        ? {
            sellingPlanGroupsByProductId: shared.sellingPlanGroupsByProductId,
        }
        : {})), (shared.requiresSellingPlan !== undefined
        ? { requiresSellingPlan: shared.requiresSellingPlan }
        : {})), (shared.requiresSellingPlanByProductId !== undefined
        ? {
            requiresSellingPlanByProductId: shared.requiresSellingPlanByProductId,
        }
        : {})), (shared.productDiscounts !== undefined
        ? { productDiscounts: shared.productDiscounts }
        : {})), (shared.carouselImages !== undefined
        ? { carouselImages: shared.carouselImages }
        : {})), (shared.imageDimensions !== undefined
        ? { imageDimensions: shared.imageDimensions }
        : {}));
}
function overlayFullOnlySharedFields(shared) {
    return Object.assign(Object.assign(Object.assign(Object.assign(Object.assign(Object.assign({}, (shared.bodyContent !== undefined
        ? { bodyContent: shared.bodyContent }
        : {})), (shared.reviewContent !== undefined
        ? { reviewContent: shared.reviewContent }
        : {})), (shared.reviewMedia !== undefined
        ? { reviewMedia: shared.reviewMedia }
        : {})), (shared.reviewAnalysis !== undefined
        ? { reviewAnalysis: shared.reviewAnalysis }
        : {})), (shared.carouselImages !== undefined
        ? { carouselImages: shared.carouselImages }
        : {})), (shared.carouselImagePerceptualHashes !== undefined
        ? {
            carouselImagePerceptualHashes: shared.carouselImagePerceptualHashes,
        }
        : {}));
}
function overlaySlimSharedFields(shared) {
    return Object.assign(Object.assign({}, (shared.productDiscounts !== undefined
        ? { productDiscounts: shared.productDiscounts }
        : {})), (shared.imageDimensions !== undefined
        ? { imageDimensions: shared.imageDimensions }
        : {}));
}
function expandSlimLink(link, sharedPayload) {
    const { sharedProductDataKey: _omitKey } = link, linkWithoutKey = __rest(link, ["sharedProductDataKey"]);
    return Object.assign(Object.assign({}, linkWithoutKey), overlaySlimSharedFields(sharedPayload));
}
function expandCardLink(link, sharedPayload) {
    const { sharedProductDataKey: _omitKey } = link, linkWithoutKey = __rest(link, ["sharedProductDataKey"]);
    const mergedVp = sharedPayload.virtualProductDisplayData != null
        ? slimVirtualProductDisplayDataMapForCard(mergeVirtualProductDisplayDataMaps(link.virtualProductDisplayData, sharedPayload.virtualProductDisplayData))
        : slimVirtualProductDisplayDataMapForCard(link.virtualProductDisplayData);
    return Object.assign(Object.assign(Object.assign({}, linkWithoutKey), overlayCommerceSharedFields(sharedPayload)), (mergedVp !== undefined
        ? { virtualProductDisplayData: mergedVp }
        : {}));
}
function expandFullLink(link, sharedPayload) {
    const { sharedProductDataKey: _omitKey } = link, linkWithoutKey = __rest(link, ["sharedProductDataKey"]);
    const virtualProductDisplayData = sharedPayload.virtualProductDisplayData != null
        ? mergeVirtualProductDisplayDataMaps(link.virtualProductDisplayData, sharedPayload.virtualProductDisplayData)
        : link.virtualProductDisplayData;
    return Object.assign(Object.assign(Object.assign(Object.assign({}, linkWithoutKey), overlayCommerceSharedFields(sharedPayload)), overlayFullOnlySharedFields(sharedPayload)), (virtualProductDisplayData !== undefined
        ? { virtualProductDisplayData }
        : {}));
}
function expandLinkWithSharedProductData(link, sharedPayloadByProductSetKey) {
    const productSetKey = link.sharedProductDataKey;
    if (productSetKey == null || productSetKey === '') {
        return link;
    }
    const sharedPayload = sharedPayloadByProductSetKey[productSetKey];
    if (!sharedPayload) {
        return link;
    }
    if (isApiSlimLandingPageLink(link)) {
        return expandSlimLink(link, sharedPayload);
    }
    if (isApiCardLandingPageLink(link)) {
        return expandCardLink(link, sharedPayload);
    }
    return expandFullLink(link, sharedPayload);
}
/**
 * Merges `sharedProductDataKey` references into each link. Call after fetching a landing page
 * or link batch that uses deduplicated VP payloads.
 */
export function applySharedProductDataToLinks(links, shared) {
    if (!isNonEmptySharedProductRecord(shared)) {
        return links;
    }
    return links.map((link) => expandLinkWithSharedProductData(link, shared));
}
function applySharedProductDataToProductMap(products, shared) {
    if (!isNonEmptySharedProductRecord(shared)) {
        return products;
    }
    const expanded = {};
    for (const [hash, link] of Object.entries(products)) {
        expanded[hash] = expandLinkWithSharedProductData(link, shared);
    }
    return expanded;
}
function resolveOrderedLinksFromProducts(hashes, products) {
    return (hashes !== null && hashes !== void 0 ? hashes : [])
        .filter((hash) => hash.trim().length > 0)
        .map((hash) => products[hash])
        .filter((link) => link != null);
}
function resolveLandingPageFromProducts(landingPage, products) {
    const expandedProducts = applySharedProductDataToProductMap(products, landingPage.sharedProductData);
    const sectionLandingPageLinks = resolveOrderedLinksFromProducts(landingPage.sectionLandingPageLinkHashes, expandedProducts);
    return Object.assign(Object.assign({}, landingPage), { landingPageLinks: resolveOrderedLinksFromProducts(landingPage.landingPageLinkHashes, expandedProducts), cartUpsells: resolveOrderedLinksFromProducts(landingPage.cartUpsellHashes, expandedProducts), sectionLandingPageLinks: sectionLandingPageLinks.length > 0
            ? sectionLandingPageLinks
            : undefined, sharedProductData: undefined });
}
/**
 * Expands landing-page-level `sharedProductData` into full links for storefront use.
 * Clears `sharedProductData` on the returned object.
 */
export function resolveApiLandingPageLinkWithSharedProductData(landingPage) {
    var _a, _b;
    const landingPageLinks = (_a = landingPage.landingPageLinks) !== null && _a !== void 0 ? _a : [];
    const cartUpsells = (_b = landingPage.cartUpsells) !== null && _b !== void 0 ? _b : [];
    const pageSharedPayloads = landingPage.sharedProductData;
    if (!isNonEmptySharedProductRecord(pageSharedPayloads)) {
        return Object.assign(Object.assign({}, landingPage), { landingPageLinks,
            cartUpsells });
    }
    return Object.assign(Object.assign({}, landingPage), { landingPageLinks: applySharedProductDataToLinks(landingPageLinks, pageSharedPayloads), cartUpsells: applySharedProductDataToLinks(cartUpsells, pageSharedPayloads), sectionLandingPageLinks: landingPage.sectionLandingPageLinks != null
            ? applySharedProductDataToLinks(landingPage.sectionLandingPageLinks, pageSharedPayloads)
            : undefined, sharedProductData: undefined });
}
/**
 * Materializes storefront link arrays from `products` + ordered hash lists.
 */
export function resolveApiLandingPage(landingPage) {
    return resolveLandingPageFromProducts(landingPage, landingPage.products);
}
